import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import ImageWithBasePath from "../common/imageWithBasePath";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  createTodo,
  deleteTodo,
  fetchUsersByClientId,
  Todo,
  updateTodo,
  User,
} from "../../api/todo";
import { RootState } from "../data/redux/store";
import { toast } from "react-toastify";
import {
  validateTodoTitle,
  validateDescription,
  validateDueDate,
  validateRequiredSelect,
} from "../../api/applicationValidation";

interface TodoModalProps {
  selectedTodo: Todo | null;
  setSelectedTodo: React.Dispatch<React.SetStateAction<Todo | null>>;
  refreshTodos: () => void;
}

const TodoModal: React.FC<TodoModalProps> = ({
  selectedTodo,
  setSelectedTodo,
  refreshTodos,
}) => {
  const [title, setTitle] = useState("");
  const [todoTitle, setTodoTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState<any>(null);
  const [priority, setPriority] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const [dueDate, setDueDate] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const clientId = useSelector((state: RootState) => state.auth.client_id);
  const [titleError, setTitleError] = useState("");
const [descError, setDescError] = useState("");
const [dueDateError, setDueDateError] = useState("");
const [assigneeError, setAssigneeError] = useState("");
const [priorityError, setPriorityError] = useState("");
const [statusError,   setStatusError]   = useState("");
const [formTouched, setFormTouched] = useState(false);
  // Pass setSelectedTodo to each todo row when "Edit" is clicked
const [deleting, setDeleting] = useState(false);
  // ✅ Fetch users based on client_id
  useEffect(() => {
    if (!clientId) return;
    if (clientId) {
      fetchUsersByClientId(clientId)
        .then(setUsers)
        .catch(() => console.error("Failed to load assignees"));
    }
  }, [clientId]);

  const markFormTouched = () => {
    if (!formTouched) {
      setFormTouched(true);
    }
  };

  // Real-time validation — only runs after form is touched
  useEffect(() => {
    if (!formTouched) {
      // Fresh form → hide all errors
      setTitleError("");
      setDescError("");
      setDueDateError("");
      setAssigneeError("");
      setPriorityError("");
      setStatusError("");
      return;
    }

    const currentTitle = selectedTodo ? title.trim() : todoTitle.trim();

    const titleValidation = validateTodoTitle(currentTitle);
    setTitleError(titleValidation.message);

    const descValidation = validateDescription(description.trim());
    setDescError(descValidation.message);

    const dueDateValidation = validateDueDate(dueDate);
    setDueDateError(dueDateValidation.message);

    setAssigneeError(validateRequiredSelect(assignee).message);
    setPriorityError(validateRequiredSelect(priority).message);
    setStatusError(validateRequiredSelect(status).message);
  }, [
    formTouched,
    todoTitle,
    title,
    description,
    dueDate,
    assignee,
    priority,
    status,
    selectedTodo,
  ]);

  // Reset formTouched when modal closes or switches mode
  useEffect(() => {
    if (!selectedTodo) {
      // Add mode — reset everything
      setTodoTitle("");
      setDescription("");
      setAssignee(null);
      setPriority(null);
      setStatus(null);
      setDueDate("");
      setFormTouched(false); // Important!
    }
  }, [selectedTodo]);

  // When editing → populate fields but DO NOT show errors immediately
  useEffect(() => {
    if (selectedTodo) {
      setTitle(selectedTodo.title || "");
      setDescription(selectedTodo.description || "");
      setAssignee({
        label: selectedTodo.user?.name || "Unassigned",
        value: selectedTodo.user?.id || null,
      });
      setPriority({
        label: selectedTodo.priority || "Medium",
        value: selectedTodo.priority || "Medium",
      });
      setStatus({
        label: selectedTodo.status || "Pending",
        value: selectedTodo.status || "Pending",
      });
      setDueDate(selectedTodo.due_date?.split("T")[0] || "");
      // Do NOT set formTouched = true here → wait for user interaction
    }
  }, [selectedTodo]);

  // ✅ Memoize options for React Select
  const assigneeOptions = useMemo(
    () =>
      users.map((user) => ({
        label: user.name,
        value: user.id,
      })),
    [users]
  );

  const statusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Onhold", label: "On hold" }, // value must match backend
    { value: "Inprogress", label: "In Progress" }, // ✅ backend expects this
    { value: "Completed", label: "Completed" },
  ];

  const priorityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
  ];

  
  // ✅ Handle Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  // Use real-time validation — no need for manual checks anymore
  if (titleError || dueDateError || !todoTitle?.trim() || !assignee || !priority || !status || !dueDate) {
    toast.error("Please fix the errors above");
    return;
  }

  try {
    await createTodo({
      title: todoTitle.trim(),
      description: description.trim(),
      priority: priority.value,
      status_: status.value,
      due_date: dueDate,
      user_id: assignee.value,
    });

    //toast.success("Todo created successfully");
    refreshTodos();

    // Reset form
    setTodoTitle("");
    setDescription("");
    setAssignee(null);
    setPriority(null);
    setStatus(null);
    setDueDate("");

    // Close modal
    document.getElementById("close-todo-modal")?.click();
  } catch (error) {
    //toast.error("Failed to create todo");
  }
};

  useEffect(() => {
    if (selectedTodo) {
      setTitle(selectedTodo.title || "");
      setDescription(selectedTodo.description || "");
      setAssignee({
        label: selectedTodo.user?.name,
        value: selectedTodo.user?.id,
      });
      setPriority({
        label: selectedTodo.priority,
        value: selectedTodo.priority,
      });
      setStatus({
        label: selectedTodo.status,
        value: selectedTodo.status,
      });
      setAssignee({
        label: selectedTodo.user?.name || "Unassigned",
        value: selectedTodo.user?.id || "",
      });
      setDueDate(selectedTodo.due_date?.split("T")[0] || "");
    }
  }, [selectedTodo]);

  const handleEditSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!selectedTodo?.todo_id) return;

  if (titleError || dueDateError || !title?.trim() || !assignee || !priority || !status || !dueDate) {
    toast.error("Please fix the errors above");
    return;
  }

  //try {
    await updateTodo(selectedTodo.todo_id, {
      title: title.trim(),
      description: description.trim(),
      priority: priority.value,
      status_: status.value,
      due_date: dueDate,
      user_id: assignee.value || null,
    });

    //toast.success("Todo updated successfully");
    refreshTodos();
    document.getElementById("close-edit-modal")?.click();
    setSelectedTodo(null);
  // } catch (error) {
  //   toast.error("Failed to update todo");
  // }
};

  const handleDelete = async () => {
    if (!selectedTodo?.todo_id) return;
    try {
      await deleteTodo(selectedTodo.todo_id);
      toast.success("Todo deleted");
      refreshTodos();
      setSelectedTodo(null);
    } catch {
      // handled
    }
  };

  useEffect(() => {
  const currentTitle = (todoTitle || title || "").trim();

  setTitleError(validateTodoTitle(currentTitle).message);
  setDescError(validateDescription(description).message);
  setDueDateError(validateDueDate(dueDate).message);

  setAssigneeError(validateRequiredSelect(assignee).message);
  setPriorityError(validateRequiredSelect(priority).message);
  setStatusError(validateRequiredSelect(status).message);
}, [todoTitle, title, description, dueDate, assignee, priority, status]);

  const getPriorityBadgeClass = (priority: string | null) => {
    if (!priority) return "badged";
    return `badged ${priority.toLowerCase()}`;
  };

  return (
    <div>
      {/* Add Todo */}
      <div className="modal fade effect-modal-show" id="note-units">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content border rounded-3 shadow-sm">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-bottom custom-modal-header">
                  <div className="page-title">
                    <h4 className="mb-0 text-primary">
                      <i className="ti ti-square-rounded-plus-filled me-2 fs-20" />Add Todo
                    </h4>
                  </div>
                  <button
                    id="close-todo-modal"
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 text-dark"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                    </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">
                          <i className="fas fa-heading me-2 text-dark"></i>Todo
                          Title
                        </label>
                        <input
  type="text"
  className={`form-control ${formTouched && titleError ? "is-invalid" : todoTitle.trim() ? "is-valid" : ""}`}
  value={todoTitle}
  onChange={(e) => setTodoTitle(e.target.value)}
  onFocus={markFormTouched}
  placeholder="Enter todo title"
/>
{formTouched && titleError && <div className="invalid-feedback">{titleError}</div>}
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          <i className="fas fa-user-tag me-2 text-dark"></i>
                          Assignee
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={assigneeOptions}
                          value={assignee}
                          onChange={setAssignee}
                          onFocus={markFormTouched}
                          placeholder="Choose"
                       className={assigneeError ? "is-invalid-select" : assignee ? "is-valid-select" : ""}
  />
 {formTouched && assigneeError && <div className="text-danger small mt-1">{assigneeError}</div>}
</div>

                      <div className="col-md-6">
                        <label className="form-label">
                          <i className="fas fa-flag me-2 text-dark"></i>Priority
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={priorityOptions}
                          value={priority}
                          onChange={setPriority}
                          onFocus={markFormTouched}
                          placeholder="Select"
                        className={priorityError ? "is-invalid-select" : priority ? "is-valid-select" : ""}
  />
  {formTouched && priorityError && <div className="text-danger small mt-1">{priorityError}</div>}
</div>

                      <div className="col-md-6">
                        <label className="form-label">
                          <i className="fas fa-tasks me-2 text-dark"></i>Status
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={statusOptions}
                          value={status}
                          onChange={setStatus}
                          onFocus={markFormTouched}
                          placeholder="Select"
                        className={statusError ? "is-invalid-select" : status ? "is-valid-select" : ""}
  />
  {formTouched && statusError && <div className="text-danger small mt-1">{statusError}</div>}
</div>

                      <div className="col-md-6">
                        <label className="form-label">
                          <i className="fas fa-calendar-alt me-2 text-dark"></i>
                          Due Date
                        </label>
                        <input
                          type="date"
                          className={`form-control ${
    formTouched && dueDateError
      ? "is-invalid"
      : formTouched && dueDate
      ? "is-valid"
      : ""
  }`}
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          onFocus={markFormTouched}
                        />
                        {formTouched && dueDateError && <div className="invalid-feedback">{dueDateError}</div>}
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          <i className="fas fa-align-left me-2 text-dark"></i>
                          Description
                        </label>
                        <textarea
  className={`form-control ${
    formTouched && descError
      ? "is-invalid"
      : formTouched && description.trim()
      ? "is-valid"
      : ""
  }`}
  value={description}
  onChange={(e) => setDescription(e.target.value)}
  onFocus={markFormTouched}
  maxLength={60}
  rows={3}
  placeholder="Optional description..."
/>
{formTouched && descError ? (
  <div className="invalid-feedback">{descError}</div>
) : description.trim() ? (
  <div className="valid-feedback">Looks good!</div>
) : null}
<p className="text-muted small mb-0 mt-1">
  Maximum 60 Characters
</p>
                      </div>
                    </div>

                    <div className="modal-footer-btn mt-4 d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button 
  type="submit" 
  className="btn btn-submit"
  disabled={!!titleError || !!dueDateError || !todoTitle?.trim() || !assignee || !priority || !status || !dueDate}
>
  Submit
</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Todo */}
      <div className="modal fade effect-modal-show" id="edit-note-units">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content border rounded-3 shadow-sm">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-bottom custom-modal-header">
                  <div className="page-title fw-3">
                    <h4 className="mb-0 text-primary">
                      <i className="ti ti-edit me-2 fs-20" />Edit Todo
                    </h4>
                  </div>
                  <button
                    id="close-edit-modal"
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 text-dark"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                    </button>
                  
                </div>

                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleEditSubmit}>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">
                          <i className="fas fa-heading me-2 text-dark"></i>Todo
                          Title
                        </label>
                        <input
  type="text"
  className={`form-control ${titleError ? "is-invalid" : title.trim() ? "is-valid" : ""}`}
  value={title}
  onChange={(e) => setTitle(e.target.value)}
  onFocus={markFormTouched}
  placeholder="Edit title"
/>
{formTouched && titleError && <div className="invalid-feedback">{titleError}</div>}
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          <i className="fas fa-user-tag me-2 text-dark"></i>
                          Assignee
                        </label>
                        <Select
                          options={assigneeOptions}
                          value={assignee}
                          onChange={setAssignee}
                          onFocus={markFormTouched}
                          placeholder="Select"
                        />
                        {formTouched && assigneeError && <div className="text-danger small mt-1">{assigneeError}</div>}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          <i className="fas fa-flag me-2 text-dark"></i>Priority
                        </label>
                        <Select
                          options={priorityOptions}
                          value={priority}
                          onChange={setPriority}
                          onFocus={markFormTouched}
                          placeholder="Priority"
                        />
                        {formTouched && priorityError && <div className="text-danger small mt-1">{priorityError}</div>}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          <i className="fas fa-tasks me-2 text-dark"></i>Status
                        </label>
                        <Select
                          options={statusOptions}
                          value={status}
                          onChange={setStatus}
                          onFocus={markFormTouched}
                          placeholder="Choose"
                        />
                        {formTouched && statusError && <div className="text-danger small mt-1">{statusError}</div>}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          <i className="fas fa-calendar-alt me-2 text-dark"></i>
                          Due Date
                        </label>
                        <input
  type="date"
  className={`form-control ${dueDateError ? "is-invalid" : dueDate ? "is-valid" : ""}`}
  value={dueDate}
  onChange={(e) => setDueDate(e.target.value)}
  onFocus={markFormTouched}
/>
{formTouched && dueDateError && <div className="invalid-feedback">{dueDateError}</div>}
                      </div>

                      <div className="col-12">
                        <label className="form-label">
                          <i className="fas fa-align-left me-2 text-dark"></i>
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          maxLength={60}
                          rows={3}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          onFocus={markFormTouched}
                        />
                        {formTouched && descError && <div className="text-danger small mt-1">{descError}</div>}
                        <p className="text-muted small mb-0">
                          Maximum 60 Characters
                        </p>
                      </div>
                    </div>

                    <div className="modal-footer-btn mt-4 d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button 
  type="submit" 
  className="btn btn-submit"
  disabled={!!titleError || !!dueDateError || !title?.trim() || !assignee || !priority || !status || !dueDate}
>
  Save Changes
</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Edit Todo */}
      {/* Delete Note */}
      <div
          className="modal fade effect-modal-show"
          id="delete-note-units"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="deleteNoteLabel"
          aria-hidden="true"
        >
        <div className="modal-dialog modal-dialog-centered ">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-danger fw-bold"
                id="deleteNoteLabel"
              >
                <i className="ti ti-alert-triangle me-2" />
                Confirm Deletion
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body text-center ">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto effect-avatar-pulse">
                <i className="ti ti-trash fs-40 text-danger" />
              </div>
              <h4 className="fw-bold text-dark mb-3">
                Delete This ToDo?
              </h4>
              <p className="text-muted fs-15 mb-4 px-4">
                This action <strong>cannot be undone</strong>. The ToDo
                will be
                <strong> permanently removed</strong>.
              </p>
              
              <div className="d-flex gap-3 justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-light px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                  onClick={() => handleDelete()}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Yes, Delete It
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* /Delete Note */}
      {/* View Note */}
      <div className="modal fade effect-modal-show" id="view-note-units" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content rounded-4 shadow-sm">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-bottom custom-modal-header pb-2 mb-3">
                  <div className="page-title edit-page-title">
                    <h4 className="mb-0 text-primary">Todo Details</h4>
                  </div>
                  <button
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 text-dark"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ><i className="ti ti-x" /></button>
                </div>

                <div className="modal-body custom-modal-body pt-2">
                  <div className="row">
                    <div className="col-12">
                      <div className="edit-head-view px-2">
                        <div className="mb-3">
                          <strong>Title:</strong>
                          <div>{selectedTodo?.title || "Untitled"}</div>
                        </div>

                        <div className="mb-3">
                          <strong>Description:</strong>
                          <div
                            className="text-muted"
                            style={{ whiteSpace: "pre-wrap" }}
                          >
                            {selectedTodo?.description || "No description"}
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Priority:</strong>
                          <div>
                            <span
                              className={getPriorityBadgeClass(
                                selectedTodo?.priority || ""
                              )}
                            >
                              <i className="fas fa-circle me-1" />
                              {selectedTodo?.priority || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Status:</strong>
                          <div>{selectedTodo?.status || "N/A"}</div>
                        </div>

                        <div className="mb-3">
                          <strong>Due Date:</strong>
                          <div>
                            {selectedTodo?.due_date
                              ? new Date(
                                  selectedTodo.due_date
                                ).toLocaleDateString()
                              : "N/A"}
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Assignee:</strong>
                          <div>{selectedTodo?.user?.name || "Unassigned"}</div>
                        </div>

                        <div className="mb-3">
                          <strong>Created At:</strong>
                          <div>
                            {selectedTodo?.created_at
                              ? new Date(
                                  selectedTodo.created_at
                                ).toLocaleString()
                              : "Unknown"}
                          </div>
                        </div>
                      </div>

                      <div className="modal-footer-btn edit-footer-menu mt-4 text-end">
                        <button
                          className="btn btn-secondary"
                          data-bs-dismiss="modal"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /View Note */}
    </div>
  );
};

export default TodoModal;
